import useColumns from '@API/project/useColumns';
import useRemoveViewMutation from '@API/project/useRemoveViewMutation';
import useUpsertLocalViewMutation from '@API/project/useUpsertLocalViewMutation';
import useUpsertViewMutation from '@API/project/useUpsertViewMutation';
import {
  ProjectColumn,
  ProjectTemplate
} from '@experiment-management-shared/types';
import { DASHBOARD_REFETCH_INTERVAL } from '@experiment-management-shared/utils/EMView';
import { DebouncedFunc, debounce } from 'lodash';
import React, { createContext, useCallback, useContext, useMemo } from 'react';

export const DASHBOARD_VIEW_AUTOSAVE_DELAY = 5000;

type RemoveTemplateData = {
  isDefaultView?: boolean;
  isSelectedTemplate: boolean;
  onError?: (e: unknown) => void;
  onSuccess: () => void;
  personalViewId?: string | null;
  projectId: string;
  viewId: string;
};

type SaveTemplate = ReturnType<typeof useUpsertViewMutation>['mutate'];

export type DashboardApiContextType = {
  columns: ProjectColumn[];
  extraColumns: ProjectColumn[];
  isColumnsSuccess: boolean;
  isLoadingColumns: boolean;
  isLoadingExtraColumns: boolean;
  isRemovingTemplate: boolean;
  isSavingTemplate: boolean;
  removeTemplate: (data: RemoveTemplateData) => void;
  saveTemplateLocally: (view: ProjectTemplate) => void;
  saveTemplateOnServer: SaveTemplate;
  saveTemplateOnServerDebounced: DebouncedFunc<SaveTemplate>;
};

export const DashboardApiContext = createContext<
  DashboardApiContextType | undefined
>(undefined);

type DashboardApiProps = {
  children: React.ReactNode;
};
export const DashboardApiProvider: React.FC<DashboardApiProps> = ({
  children
}) => {
  const { mutate: saveTemplateOnLocal } = useUpsertLocalViewMutation();
  const {
    mutate: saveTemplateOnServer,
    isLoading: isSavingTemplate
  } = useUpsertViewMutation();
  const {
    mutate: removeServerTemplate,
    isLoading: isRemovingTemplate
  } = useRemoveViewMutation();

  const saveTemplateOnServerDebounced = useMemo(
    () => debounce(saveTemplateOnServer, DASHBOARD_VIEW_AUTOSAVE_DELAY),
    []
  );

  const { data: extraColumns, isLoading: isLoadingExtraColumns } = useColumns(
    { extraCols: true },
    // @todo: why not reusing dashboard interval hook?
    { refetchInterval: DASHBOARD_REFETCH_INTERVAL, refetchOnMount: true }
  );

  const {
    data: columns,
    isLoading: isLoadingColumns,
    isSuccess: isColumnsSuccess
  } = useColumns(
    {},
    // @todo: why not reusing dashboard interval hook?
    { refetchInterval: DASHBOARD_REFETCH_INTERVAL, refetchOnMount: true }
  );

  const saveTemplateLocally = useCallback(
    (view: ProjectTemplate) => {
      saveTemplateOnLocal({ view });
    },
    [saveTemplateOnLocal]
  );

  const removeTemplate = useCallback(
    ({
      isDefaultView,
      isSelectedTemplate,
      onSuccess,
      onError,
      personalViewId,
      projectId,
      viewId
    }: RemoveTemplateData) => {
      if (isSelectedTemplate) {
        saveTemplateOnServerDebounced.cancel();
      }

      removeServerTemplate(
        { isDefaultView, personalViewId, projectId, viewId },
        { onSuccess, onError }
      );
    },
    [removeServerTemplate, saveTemplateOnServerDebounced]
  );

  return (
    <DashboardApiContext.Provider
      value={{
        columns,
        extraColumns,
        isColumnsSuccess,
        isLoadingColumns,
        isLoadingExtraColumns,
        isRemovingTemplate,
        isSavingTemplate,
        removeTemplate,
        saveTemplateLocally,
        saveTemplateOnServer,
        saveTemplateOnServerDebounced
      }}
    >
      {children}
    </DashboardApiContext.Provider>
  );
};

export const useDashboardApiContext = (): DashboardApiContextType => {
  const ctx = useContext(DashboardApiContext);

  if (ctx === undefined) {
    throw new Error(
      'useDashboardApiContext must be used within a DashboardApiProvider'
    );
  }

  return ctx;
};
